import { Link } from "react-router-dom";
import type { AccountBalance, Currency, LedgerAccount } from "../../entities/ledger/types";
import { money } from "../../shared/lib/format";
import { debtSourceLabel, observedAt, readAccount } from "./accountMoney";

interface AccountTileProps {
  account: LedgerAccount;
  balance?: AccountBalance;
  reportingCurrency?: Currency;
  // False when other accounts in the same grid carry the same label, which
  // is the common case for several cards on one IBAN: printing it then adds
  // a line that looks like an identifier and identifies nothing.
  labelIsDistinct?: boolean;
}

// A tile is read for one thing: what the account holds. What it owes by its
// own history is a separate, labelled figure and never a minus sign on the
// balance; a Borrowing it is the facility of shows its own debt beside it; what a
// provider reports as spendable is an observation shown beside both, with the
// moment it was observed, and never in place of the balance. Every figure is
// labelled because an unlabelled number on a credit card is easy to misread.
export function AccountTile({ account, balance, reportingCurrency, labelIsDistinct = true }: AccountTileProps) {
  const reading = readAccount(account, balance);
  const reporting = balance?.position?.reporting;
  const book = reading.book
    ? `${money(reading.book.value.minor, reading.book.value.currency)} ${reading.book.owed ? "debt, book value" : "book value"}`
    : reading.bookUnavailable ? "No rate to value this account in the book currency" : null;
  const reported = reporting && reportingCurrency && reportingCurrency !== account.currency
    && reporting.held.currency !== account.currency
    ? reporting.owed.minor > 0
      ? `${money(reporting.owed.minor, reporting.owed.currency)} owed in the reporting currency`
      : `${money(reporting.held.minor, reporting.held.currency)} in the reporting currency`
    : null;

  return (
    <Link className="account-tile" to={`/accounts/${encodeURIComponent(account.id)}`} aria-label={`${account.name} account`}>
      <div className="account-tile-top">
        <strong title={account.name}>{account.name}</strong>
        <span className="account-currency">{account.currency}</span>
      </div>
      <span className="account-total">
        <small className="account-figure-label">Balance</small>
        {money(reading.balance.minor, reading.balance.currency)}
      </span>
      {reading.debt || reading.available || reading.borrowings.length > 0 ? (
        <dl className="account-extras">
          {reading.debt ? (
            <div>
              <dt>Account debt</dt>
              <dd className="owed">
                {money(reading.debt.minor, reading.debt.currency)}
                {reading.unattributed ? <small> not attributed to a Borrowing</small> : null}
              </dd>
            </div>
          ) : null}
          {reading.borrowings.map((linked) => (
            <div key={linked.agreementId}>
              <dt>Borrowing debt · {linked.creditor}</dt>
              <dd>
                {linked.debt ? money(linked.debt.minor, linked.debt.currency) : <span className="unknown">Unknown</span>}
                {linked.debt ? <small> {debtSourceLabel(linked.debtSource)}</small> : null}
              </dd>
            </div>
          ))}
          {reading.available ? (
            <div>
              <dt>Available to spend</dt>
              <dd>
                {money(reading.available.amount.minor, reading.available.amount.currency)}
                {reading.available.observedAt ? <small> observed {observedAt(reading.available.observedAt)}</small> : null}
              </dd>
            </div>
          ) : null}
        </dl>
      ) : null}
      <div className="account-tile-foot">
        {account.displayLabel && labelIsDistinct ? <span className="account-reference" title={account.reference ?? account.displayLabel}>{account.displayLabel}</span> : null}
      </div>
      <small>{book ?? reported ?? "Statement, balance and new operations →"}</small>
    </Link>
  );
}

// A label earns its line only when no sibling in the same grid repeats it.
export function distinctLabels(accounts: LedgerAccount[]): Set<string> {
  const seen = new Map<string, number>();
  for (const account of accounts) {
    const label = account.displayLabel ?? "";
    if (label !== "") seen.set(label, (seen.get(label) ?? 0) + 1);
  }
  return new Set([...seen].filter(([, count]) => count === 1).map(([label]) => label));
}
